import { useThree } from '@react-three/fiber';
import {
    memo,
    useCallback,
    useLayoutEffect,
    useMemo,
    useRef,
} from 'react';
import type { WebGLProgramParametersWithUniforms } from 'three';
import type { ModelViewerPedestalLayout } from '../Pedestal';

interface Props {
    layout: ModelViewerPedestalLayout
}

const MARBLE_SHADER_CACHE_KEY = 'studio-pedestal-marble-v2';

// The procedural field is injected into MeshPhysicalMaterial instead of using
// a separate ShaderMaterial, preserving Three's Studio lights, IBL, clearcoat,
// color management, and future physical-material fixes.
const MARBLE_SHADER_PARAMETERS = `
    uniform float pedestalMarbleScale;
    varying vec3 vPedestalMarblePosition;

    float pedestalMarbleHash(vec3 point) {
        point = fract(point * 0.1031);
        point += dot(point, point.yzx + 33.33);
        return fract((point.x + point.y) * point.z);
    }

    float pedestalMarbleNoise(vec3 point) {
        vec3 cell = floor(point);
        vec3 localPoint = fract(point);
        localPoint = localPoint * localPoint * (3.0 - 2.0 * localPoint);

        return mix(
            mix(
                mix(
                    pedestalMarbleHash(cell + vec3(0.0, 0.0, 0.0)),
                    pedestalMarbleHash(cell + vec3(1.0, 0.0, 0.0)),
                    localPoint.x
                ),
                mix(
                    pedestalMarbleHash(cell + vec3(0.0, 1.0, 0.0)),
                    pedestalMarbleHash(cell + vec3(1.0, 1.0, 0.0)),
                    localPoint.x
                ),
                localPoint.y
            ),
            mix(
                mix(
                    pedestalMarbleHash(cell + vec3(0.0, 0.0, 1.0)),
                    pedestalMarbleHash(cell + vec3(1.0, 0.0, 1.0)),
                    localPoint.x
                ),
                mix(
                    pedestalMarbleHash(cell + vec3(0.0, 1.0, 1.0)),
                    pedestalMarbleHash(cell + vec3(1.0, 1.0, 1.0)),
                    localPoint.x
                ),
                localPoint.y
            ),
            localPoint.z
        );
    }

    float pedestalMarbleFbm(vec3 point) {
        float value = 0.0;
        float amplitude = 0.5;

        for(int octave = 0; octave < 4; octave++) {
            value += amplitude * pedestalMarbleNoise(point);
            point = point * 2.03 + vec3(11.7, 7.3, 5.9);
            amplitude *= 0.5;
        }

        return value / 0.9375;
    }

    vec3 pedestalMarbleSurface(
        vec3 point,
        out float cloud,
        out float vein
    ) {
        float shapeNoise = pedestalMarbleNoise(
            point * 0.44 + vec3(3.7, 8.1, 1.9)
        );
        vec3 warpedPoint = point + vec3(
            shapeNoise - 0.5,
            pedestalMarbleNoise(point * 0.83 + vec3(8.4, 1.6, 4.2)) - 0.5,
            pedestalMarbleNoise(point * 0.91 + vec3(2.3, 6.8, 9.1)) - 0.5
        ) * 0.78;
        float broadNoise = pedestalMarbleFbm(
            warpedPoint * 0.76 + vec3(2.1, 0.4, 1.3)
        );
        float detailNoise = pedestalMarbleFbm(
            warpedPoint * 2.55 + vec3(4.7, 2.8, 6.2)
        );
        cloud = clamp(broadNoise * 0.72 + detailNoise * 0.28, 0.0, 1.0);

        float veinField = dot(
            warpedPoint,
            vec3(0.72, 0.18, 0.42)
        )
            + broadNoise * 1.32
            + detailNoise * 0.26;
        float veinDistance = abs(sin(veinField * PI));
        float veinHalo = 1.0 - smoothstep(
            0.075,
            0.32,
            veinDistance
        );
        float veinCore = 1.0 - smoothstep(
            0.016,
            0.09,
            veinDistance
        );
        float broadVein = (
            veinHalo * 0.46
            + veinCore * 0.62
        ) * mix(0.72, 1.0, detailNoise);

        float fineVeinField = dot(
            warpedPoint,
            vec3(-0.22, 0.28, 0.82)
        ) * 1.85
            + detailNoise * 0.62
            + broadNoise * 0.22;
        float fineVeinDistance = abs(sin(fineVeinField * PI));
        float fineVein = (
            (1.0 - smoothstep(0.05, 0.22, fineVeinDistance)) * 0.34
            + (1.0 - smoothstep(0.012, 0.065, fineVeinDistance)) * 0.46
        );
        vein = clamp(max(broadVein, fineVein * 0.68), 0.0, 1.0);

        vec3 stoneColor = vec3(0.79, 0.81, 0.84);
        vec3 cloudColor = vec3(0.44, 0.49, 0.56);
        vec3 veinColor = vec3(0.17, 0.20, 0.27);
        stoneColor = mix(
            stoneColor,
            cloudColor,
            smoothstep(0.24, 0.8, cloud) * 0.34
        );

        return mix(
            stoneColor,
            veinColor,
            smoothstep(0.05, 0.92, vein) * 0.74
        );
    }
`;

const MARBLE_COLOR_FRAGMENT = `
    float pedestalMarbleCloud = 0.0;
    float pedestalMarbleVein = 0.0;
    vec3 pedestalMarbleColor = pedestalMarbleSurface(
        vPedestalMarblePosition * pedestalMarbleScale,
        pedestalMarbleCloud,
        pedestalMarbleVein
    );
    diffuseColor.rgb *= pedestalMarbleColor;
`;

/**
 * Seamless object-space marble. A physical material supplies the Studio
 * lighting and clearcoat, while a domain-warped noise field creates layered
 * Carrara clouds and veins across cylinder caps, walls, and box
 * bevels without relying on their incompatible UV layouts.
 */
export default memo(function PedestalMarbleMaterial({layout}:Props) {
    const invalidate = useThree(state => state.invalidate);
    const shaderRef = useRef<WebGLProgramParametersWithUniforms>();
    const marbleScale = useMemo(() => (
        3.15 / Math.max(layout.width, layout.depth, 0.001)
    ), [layout.depth, layout.width]);
    const marbleScaleRef = useRef(marbleScale);
    marbleScaleRef.current = marbleScale;

    const prepareShader = useCallback((
        shader:WebGLProgramParametersWithUniforms,
    ) => {
        shader.uniforms.pedestalMarbleScale = {
            value: marbleScaleRef.current,
        };
        // Object-space position makes the pattern continuous across box bevels
        // and cylinder caps whose UV islands do not line up.
        shader.vertexShader = shader.vertexShader
            .replace(
                '#include <common>',
                `#include <common>
                varying vec3 vPedestalMarblePosition;`,
            )
            .replace(
                '#include <begin_vertex>',
                `#include <begin_vertex>
                vPedestalMarblePosition = position;`,
            );
        // Replace map sampling with procedural color, then perturb roughness
        // slightly so clouds and veins also respond in specular highlights.
        shader.fragmentShader = shader.fragmentShader
            .replace(
                '#include <common>',
                `#include <common>
                ${MARBLE_SHADER_PARAMETERS}`,
            )
            .replace('#include <map_fragment>', MARBLE_COLOR_FRAGMENT)
            .replace(
                '#include <roughnessmap_fragment>',
                `#include <roughnessmap_fragment>
                roughnessFactor = clamp(
                    roughnessFactor
                    + (pedestalMarbleCloud - 0.5) * 0.07
                    + pedestalMarbleVein * 0.03,
                    0.12,
                    0.3
                );`,
            );
        shaderRef.current = shader;
    }, []);

    useLayoutEffect(() => {
        const scaleUniform = (
            shaderRef.current?.uniforms.pedestalMarbleScale
        );
        if(scaleUniform) {
            scaleUniform.value = marbleScale;
            invalidate();
        }
    }, [invalidate, marbleScale]);

    return <meshPhysicalMaterial
        clearcoat={0.95}
        clearcoatRoughness={0.065}
        color="#ffffff"
        customProgramCacheKey={() => MARBLE_SHADER_CACHE_KEY}
        dithering
        envMapIntensity={1.3}
        ior={1.52}
        metalness={0}
        onBeforeCompile={prepareShader}
        roughness={0.2}
        specularIntensity={1}
    />;
});
